import { Mutex } from "async-mutex";
import { jwtDecode } from "jwt-decode";
import { useCallback } from "react";

import { useApiUrl } from "@luna-restaurant/school-api";

import { useAuthApi } from "@/services/authApi";
import { authStorage } from "@/services/authStorage";
import { notifySessionExpired } from "@/services/sessionExpiryEvents";

type ApiUrlOptions = {
  query?: string[][] | Record<string, string> | string | URLSearchParams;
};

type RequestOptions = RequestInit & ApiUrlOptions;

type Request = <T = unknown>(
  path: string,
  options?: RequestOptions,
) => Promise<T | undefined>;

type AccessTokenState = {
  token: string | null;
  failureReason: "expired" | "refresh_failed" | "missing" | null;
};

type ApiError = Error & {
  status: number;
  url: string;
  method: string;
  responseBody?: unknown;
  serverTitle?: string;
};

export const isApiError = (error: unknown): error is ApiError =>
  error instanceof Error &&
  typeof (error as ApiError).status === "number" &&
  typeof (error as ApiError).url === "string" &&
  typeof (error as ApiError).method === "string";

const MAX_ERROR_BODY_CHARS = 2048;

const ALLOWED_ERROR_BODY_KEYS = [
  "title",
  "detail",
  "type",
  "status",
  "instance",
  "code",
  "violations",
  "errors",
  "hydra:title",
  "hydra:description",
] as const;

const ALLOWED_VIOLATION_KEYS = ["propertyPath", "message", "code"] as const;
const ALLOWED_NESTED_ERROR_KEYS = [
  "propertyPath",
  "message",
  "code",
  "field",
] as const;

const sanitizeUrl = (url: string): string => {
  try {
    const parsed = new URL(url);
    return `${parsed.origin}${parsed.pathname}`;
  } catch {
    const queryStart = url.indexOf("?");
    const fragmentStart = url.indexOf("#");
    const cuts = [queryStart, fragmentStart].filter((index) => index >= 0);
    return cuts.length > 0 ? url.slice(0, Math.min(...cuts)) : url;
  }
};

const pickKeys = <K extends string>(
  source: Record<string, unknown>,
  keys: readonly K[],
): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  for (const key of keys) {
    if (key in source) {
      result[key] = source[key];
    }
  }
  return result;
};

const sanitizeNestedEntry = (
  entry: unknown,
  keys: readonly string[],
): unknown => {
  if (entry && typeof entry === "object" && !Array.isArray(entry)) {
    return pickKeys(entry as Record<string, unknown>, keys);
  }
  return entry;
};
const truncateString = (value: string): string =>
  value.length <= MAX_ERROR_BODY_CHARS
    ? value
    : `${value.slice(0, MAX_ERROR_BODY_CHARS)}…[truncated ${
        value.length - MAX_ERROR_BODY_CHARS
      } chars]`;

const summarizeErrorBody = (body: unknown): unknown => {
  if (body == null) return undefined;
  if (typeof body === "string") {
    return truncateString(body);
  }
  if (typeof body !== "object") return undefined;

  const record = body as Record<string, unknown>;
  const summary: Record<string, unknown> = {};
  for (const key of ALLOWED_ERROR_BODY_KEYS) {
    if (!(key in record)) continue;
    const value = record[key];
    if (key === "violations" && Array.isArray(value)) {
      summary[key] = value.map((entry) =>
        sanitizeNestedEntry(entry, ALLOWED_VIOLATION_KEYS),
      );
    } else if (key === "errors" && Array.isArray(value)) {
      summary[key] = value.map((entry) =>
        sanitizeNestedEntry(entry, ALLOWED_NESTED_ERROR_KEYS),
      );
    } else {
      summary[key] = value;
    }
  }
  if (Object.keys(summary).length === 0) return undefined;

  try {
    const serialized = JSON.stringify(summary);
    if (serialized.length <= MAX_ERROR_BODY_CHARS) return summary;
    return truncateString(serialized);
  } catch {
    return undefined;
  }
};

const parseErrorBody = async (
  response: Response,
): Promise<{ body: unknown; serverTitle?: string }> => {
  try {
    const text = await response.text();
    if (!text) return { body: undefined };
    try {
      const parsed = JSON.parse(text) as unknown;
      const title = (parsed as { title?: unknown } | null)?.title;
      return {
        body: summarizeErrorBody(parsed),
        serverTitle: typeof title === "string" ? title : undefined,
      };
    } catch {
      return { body: summarizeErrorBody(text) };
    }
  } catch {
    return { body: undefined };
  }
};

const buildApiError = (
  response: Response,
  url: string,
  method: string,
  serverTitle: string | undefined,
  responseBody: unknown,
): ApiError => {
  const safeUrl = sanitizeUrl(url);
  const summary = serverTitle ? `: ${serverTitle}` : "";
  const error = new Error(
    `${method} ${safeUrl} failed with status ${response.status}${summary}`,
  ) as ApiError;
  error.status = response.status;
  error.url = safeUrl;
  error.method = method;
  error.serverTitle = serverTitle;
  error.responseBody = responseBody;
  return error;
};

const refreshMutex = new Mutex();

export const useRequest = (): Request => {
  const apiUrl = useApiUrl();
  const { refreshAccessToken, logout } = useAuthApi();

  const execute = useCallback(
    async (url: string, token: string | null, options?: RequestInit) => {
      const headers = {
        Accept: "application/ld+json",
        ...(options?.headers ?? {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      } as Record<string, string>;

      const response = await fetch(url, {
        ...options,
        headers,
      });

      return response;
    },
    [],
  );

  const checkTokenExpiration = useCallback((token: string | null): boolean => {
    if (!token) return false;
    try {
      const payload = jwtDecode<{ exp?: number }>(token);
      if (typeof payload.exp !== "number") {
        return true;
      }
      return Date.now() >= payload.exp * 1000;
    } catch (err) {
      console.error("Parsing JWT token payload failed", err);
      return true;
    }
  }, []);

  const getAccessToken = useCallback(
    async (
      forceRefresh = false,
      tokenOverride?: string | null,
    ): Promise<AccessTokenState> => {
      let token = await authStorage.getAccessToken();
      const tokenForExpiry = tokenOverride ?? token;
      const isTokenExpired = checkTokenExpiration(tokenForExpiry);

      if (forceRefresh || !token || isTokenExpired) {
        const release = await refreshMutex.acquire();
        try {
          // Another request might have refreshed while we were waiting.
          const latestToken = await authStorage.getAccessToken();
          const latestTokenExpired = checkTokenExpiration(latestToken);
          const refreshedByAnotherRequest =
            Boolean(latestToken) &&
            !latestTokenExpired &&
            (!forceRefresh || !tokenOverride || latestToken !== tokenOverride);

          if (refreshedByAnotherRequest) {
            return { token: latestToken, failureReason: null };
          }

          const refreshedToken = await refreshAccessToken();
          if (refreshedToken) {
            token = refreshedToken;
          } else {
            if (!tokenForExpiry) {
              return { token: null, failureReason: "missing" };
            }

            return {
              token: null,
              failureReason: isTokenExpired ? "expired" : "refresh_failed",
            };
          }
        } finally {
          release();
        }
      }

      return { token, failureReason: null };
    },
    [refreshAccessToken, checkTokenExpiration],
  );

  return useCallback<Request>(
    async (path, options) => {
      const url = apiUrl(path, options);
      const method = (options?.method ?? "GET").toUpperCase();

      const accessState = await getAccessToken();
      let token = accessState.token;

      if (accessState.failureReason === "expired") {
        notifySessionExpired();
        throw new Error("SessionExpired");
      }

      if (!token) {
        await logout();

        if (accessState.failureReason === "refresh_failed") {
          notifySessionExpired();
        }

        throw new Error("Unauthorized");
      }

      let response = await execute(url, token, options);

      if (response.status === 401) {
        const refreshedState = await getAccessToken(true, token);
        token = refreshedState.token;

        if (refreshedState.failureReason === "expired") {
          notifySessionExpired();
          throw new Error("SessionExpired");
        }

        if (token) {
          response = await execute(url, token, options);
        } else {
          const { body, serverTitle } = await parseErrorBody(response);
          await logout();
          notifySessionExpired();
          throw buildApiError(response, url, method, serverTitle, body);
        }
      }

      if (!response.ok) {
        const { body, serverTitle } = await parseErrorBody(response);
        const error = buildApiError(response, url, method, serverTitle, body);
        if (response.status === 401) {
          await logout();
          notifySessionExpired();
        }
        throw error;
      }

      if (response.status === 204) {
        return undefined;
      }

      const acceptHeader =
        (options?.headers as Record<string, string> | undefined)?.Accept ??
        (options?.headers as Record<string, string> | undefined)?.accept;
      if (acceptHeader && acceptHeader.includes("application/pdf")) {
        return await response.blob();
      }

      return await response.json();
    },
    [apiUrl, execute, getAccessToken, logout],
  );
};
